import React, { useEffect, useState } from 'react';
import * as Styled from './styled';
import { useLocale } from 'locale';
import CopyToClipboard from 'react-copy-to-clipboard';
import { useWindowSize } from 'hooks/useWindowSize';
import { SmallText } from 'ds/components/typography';
import { ShareType, shareActionTypeToIcon } from 'helpers/share-helpers';
import { ShareButtonLocal } from 'components/navigation/share/ShareButton';
import { memoize, noop } from 'lodash';
import { SelectItemProps } from 'ds/components/input/Select';
import DotsIcon from 'assets/svg/menu_dots.svg';
import { isServer } from 'utils';
import { useShareHandler } from 'screens/AgentOfficePages/common/sections/header/common/useShareHandler';
import withEvents from 'analytics/withEvents';
import { useRoute } from 'react-router5';
import { Route } from 'config/routes';
import { PageDetails } from 'screens/AgentOfficePages/types';
import { getAgentOfficeCategory } from 'screens/AgentOfficePages/helper';

export const TOOLTIP_DELAY = 4000;

const SHARE_DESKTOP_OPTIONS: SelectItemProps[] = [
  {
    value: ShareType.Email,
    label: ShareType.Email,
    icon: shareActionTypeToIcon[ShareType.Email],
  },
  {
    value: ShareType.Twitter,
    label: ShareType.Twitter,
    icon: shareActionTypeToIcon[ShareType.Twitter],
  },
  {
    value: ShareType.Facebook,
    label: ShareType.Facebook,
    icon: shareActionTypeToIcon[ShareType.Facebook],
  },
  {
    value: ShareType.FacebookMessenger,
    label: ShareType.FacebookMessenger,
    icon: shareActionTypeToIcon[ShareType.FacebookMessenger],
  },
];

const WINDOW_WIDTH_FOR_SHARE_POSITION = 1480;

const genOptions = memoize((t, isMobile) => SHARE_DESKTOP_OPTIONS.filter(o => isMobile || !o.isMobileOnly).map(({ value, icon }) => ({
  value,
  icon,
  label: t('select.share.label', { label: value }),
})));

interface SocialProps {
  name: string;
  source?: string;
  pageDetails?: PageDetails;
  onShareClick?: (event: string, category: string, source: string, pageDetails: PageDetails) => void;
  onShareOptionClick?: (event: string, category: string, source: string, pageDetails: PageDetails, method: string) => void;
}

export const shareMethodToEvent = {
  [ShareType.CopyLink]: 'copy_link',
  [ShareType.Email]: 'email',
  [ShareType.Facebook]: 'facebook',
  [ShareType.FacebookMessenger]: 'messenger',
  [ShareType.Twitter]: 'twitter',
  [ShareType.WhatsApp]: 'whatsapp',
};

export const SocialBase: React.FC<SocialProps>  = ({ name, onShareClick = noop, source, pageDetails, onShareOptionClick = noop }) => {
  const { t } = useLocale();

  const [ windowWidth ] = useWindowSize();
  const [ copied, setCopied ] = useState<string>(null);
  const shareHandler = useShareHandler(name);
  const { route } = useRoute();
  const isOfficePage = route.name === Route.OfficePage;
  const event = `${getAgentOfficeCategory(isOfficePage)}_share_click`;
  const eventMethod = `${getAgentOfficeCategory(isOfficePage)}_share_method`;
  const category = getAgentOfficeCategory(isOfficePage);
  const eventShareHandler = () => {
    onShareClick(event, category, source, pageDetails);
  };

  const handleOnCopy = React.useCallback((position: string) => {
    onShareOptionClick(eventMethod, category, source, pageDetails, shareMethodToEvent[ShareType.CopyLink]);
    if (!copied || copied !== position) setCopied(position);
    return false;
  }, [ copied, setCopied ]);

  const renderTooltip = (position: string) => (copied === position ? (
    <Styled.CopyTooltip>
      <SmallText>{t('content.copied')}</SmallText>
    </Styled.CopyTooltip>
  ) : null);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (copied) {
      timer = setTimeout(() => setCopied(null), TOOLTIP_DELAY);
    }
    return () => clearTimeout(timer);
  }, [ copied, setCopied ]);

  const handleShare = (shareType: ShareType) => {
    onShareOptionClick(eventMethod, category, source, pageDetails, shareMethodToEvent[shareType]);
    shareHandler(shareType);
  };


  return (
    <Styled.SocialSection>
      <Styled.SocialTitle>{t('tooltip.share')}</Styled.SocialTitle>
      <div style={{ position: 'relative' }}>
        <CopyToClipboard text={isServer() ? null : window.location.href} onCopy={() => handleOnCopy('share')}>
          <Styled.SocialLink>
            {shareActionTypeToIcon[ShareType.CopyLink]}
          </Styled.SocialLink>
        </CopyToClipboard>
        {renderTooltip('share')}
      </div>
      <ShareButtonLocal
        dropdownStyles={windowWidth > WINDOW_WIDTH_FOR_SHARE_POSITION ? { top: 16, right: 16 } : { top: 16, right: 'auto', left: 16 }}
        shareOptions={genOptions(t, true)}
        shouldStayOpen={() => false}
        shouldResetSelectedValue={() => false}
        handleShare={handleShare}
      >
        <Styled.SocialLink onClick={eventShareHandler}>
          <DotsIcon />
        </Styled.SocialLink>
      </ShareButtonLocal>
    </Styled.SocialSection>
  );
};

export const Social = withEvents<SocialProps>((sendEvent) => ({
  onShareClick: (event: string, category: string, source: string, pageDetails: PageDetails) => {
    sendEvent(event, category, {
      ...pageDetails,
      event: {
        source,
      },
    });
  },
  onShareOptionClick: (event: string, category: string, source: string, pageDetails: PageDetails, method: string) => {
    sendEvent(event, category, {
      ...pageDetails,
      event: {
        source,
        share_method: method,
      },
    });
  },
}))(SocialBase);
